// ============================================================================
// Service: SafetyManager365 Administrator Functions (Control Tower)
// ============================================================================

export interface SystemRole {
  roleKey: string;
  name: string;
  module: 'GENERAL' | 'REPORTING' | 'INVESTIGATION' | 'RISK' | 'QUALITY';
  description?: string | null;
}

export interface UserWithRoles {
  id: string;
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  isActive: boolean;
  roles: string[];
}

export interface AircraftRegistration {
  registration: string;
  aircraftType: string;
  serialNumber?: string | null;
  operatorName?: string | null;
  isActive: boolean;
}

export interface DistributionGroup {
  id: number;
  name: string;
  description?: string | null;
  isDeleted: boolean;
  memberUserIds: string[];
  reportKindIds: string[];
}

export interface SystemAdminAuditLog {
  id: number;
  adminUserId: string;
  entityType: string;
  entityId?: string | null;
  action: string;
  oldValue?: any;
  newValue?: any;
  timestampUtc: string;
}

const BASE_URL = '/api/v1/admin';

export const adminFunctionsService = {
  // 1. Roles & Users
  async getRoles(): Promise<SystemRole[]> {
    const res = await fetch(`${BASE_URL}/roles`);
    return res.json();
  },

  async getUsers(search?: string): Promise<UserWithRoles[]> {
    const url = search ? `${BASE_URL}/users?search=${encodeURIComponent(search)}` : `${BASE_URL}/users`;
    const res = await fetch(url);
    return res.json();
  },

  async createUser(data: {
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    roles?: string[];
  }): Promise<UserWithRoles> {
    const res = await fetch(`${BASE_URL}/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async assignUserRoles(userId: string, roles: string[]): Promise<UserWithRoles> {
    const res = await fetch(`${BASE_URL}/users/${userId}/roles`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ roles }),
    });
    return res.json();
  },

  // 2. Aircraft Registrations
  async getAircraft(): Promise<AircraftRegistration[]> {
    const res = await fetch(`${BASE_URL}/aircraft`);
    return res.json();
  },

  async createAircraft(data: {
    registration: string;
    aircraft_type: string;
    serial_number?: string;
    operator_name?: string;
  }): Promise<AircraftRegistration> {
    const res = await fetch(`${BASE_URL}/aircraft`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async softDeleteAircraft(registration: string): Promise<{ success: boolean }> {
    const res = await fetch(`${BASE_URL}/aircraft/${registration}`, {
      method: 'DELETE',
    });
    return res.json();
  },

  // 3. Reporting Administration: Distribution Groups
  async getDistributionGroups(includeDeleted = false): Promise<DistributionGroup[]> {
    const res = await fetch(`${BASE_URL}/reporting/distribution-groups?include_deleted=${includeDeleted}`);
    return res.json();
  },

  async createDistributionGroup(data: {
    name: string;
    description?: string;
    member_user_ids?: string[];
    report_kind_ids?: string[];
  }): Promise<DistributionGroup> {
    const res = await fetch(`${BASE_URL}/reporting/distribution-groups`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async updateDistributionGroup(
    id: number,
    data: {
      name?: string;
      description?: string;
      member_user_ids?: string[];
      report_kind_ids?: string[];
    }
  ): Promise<DistributionGroup> {
    const res = await fetch(`${BASE_URL}/reporting/distribution-groups/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return res.json();
  },

  async deleteDistributionGroup(id: number): Promise<{ success: boolean }> {
    const res = await fetch(`${BASE_URL}/reporting/distribution-groups/${id}`, {
      method: 'DELETE',
    });
    return res.json();
  },

  // 4. Taxonomy Copy
  async copyClassificationSettings(
    sourceClassificationId: number,
    targetClassificationId: number
  ): Promise<any> {
    const res = await fetch(`${BASE_URL}/reporting/event-classifications/copy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source_classification_id: sourceClassificationId,
        target_classification_id: targetClassificationId,
      }),
    });
    return res.json();
  },

  // 5. Relate Reports
  async relateReports(
    masterReportId: number,
    relatedReportId: number,
    reasonComment: string
  ): Promise<any> {
    const res = await fetch(`${BASE_URL}/reporting/relate-reports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        master_report_id: masterReportId,
        related_report_id: relatedReportId,
        reason_comment: reasonComment,
      }),
    });
    return res.json();
  },

  // 6. Audit Logs
  async getAuditLogs(entityType?: string, adminUserId?: string): Promise<SystemAdminAuditLog[]> {
    const params = new URLSearchParams();
    if (entityType) params.append('entity_type', entityType);
    if (adminUserId) params.append('admin_user_id', adminUserId);
    const res = await fetch(`${BASE_URL}/audit-logs?${params.toString()}`);
    return res.json();
  },
};
